import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Folder as FolderIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useCopy } from '../contexts/CopyContext';
import LoadingAnimation from './LoadingAnimation';
import projectsService from '../services/projectsService';
import { Revision } from '../types';

interface Project {
  id: number;
  name: string;
  revisions: Revision[];
}

interface DragOverlayProps {
  isVisible: boolean;
  onDrop: (projectId: number, revisionId: number) => void;
  onCancel: () => void;
}

const DragOverlay: React.FC<DragOverlayProps> = ({ isVisible, onDrop, onCancel }) => {
  const { copiedSandbox } = useCopy();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedProject, setExpandedProject] = useState<number | null>(null);


  useEffect(() => {
    if (isVisible) {
      loadProjects();
    }
  }, [isVisible]);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const projectsData = await projectsService.getProjects();

      // Load revisions for each project
      const projectsWithRevisions = await Promise.all(
        projectsData.map(async (project) => {
          try {
            const revisions = await projectsService.getRevisions(project.id);
            return {
              ...project,
              revisions: revisions || []
            };
          } catch (error) {
            console.error(`Failed to load revisions for project ${project.id}:`, error);
            return { ...project, revisions: [] };
          }
        })
      );
      setProjects(projectsWithRevisions);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRevisionDrop = (projectId: number, revisionId: number) => {
    onDrop(projectId, revisionId);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onCancel();
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <Paper
        elevation={24}
        sx={{
          width: '100%',
          maxWidth: 800,
          maxHeight: '80vh',
          overflow: 'hidden',
          borderRadius: 3,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h5" component="h2" sx={{ mb: 1, fontWeight: 600 }}>
            📋 Copy Sandbox
          </Typography>
          {copiedSandbox && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DescriptionIcon color="primary" />
              <Typography variant="body1">
                Copying: <strong>{copiedSandbox.name}</strong>
              </Typography>
              <Chip
                label={copiedSandbox.software}
                size="small"
                color="secondary"
                variant="outlined"
              />
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Drag and drop into a revision below, or press Escape to cancel
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <LoadingAnimation />
            </Box>
          ) : (
            <Box>
              {projects.map((project) => (
                <Accordion
                  key={project.id}
                  expanded={expandedProject === project.id}
                  onChange={() => setExpandedProject(
                    expandedProject === project.id ? null : project.id
                  )}
                  sx={{
                    mb: 1,
                    '&:before': { display: 'none' },
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: 'background.paper',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FolderIcon color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        {project.name}
                      </Typography>
                      <Chip
                        label={`${project.revisions.length} revisions`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ p: 0 }}>
                    <Box sx={{ display: 'grid', gap: 1, p: 1 }}>
                      {project.revisions.map((revision) => (
                        <Paper
                          key={revision.id}
                          sx={{
                            p: 2,
                            cursor: 'pointer',
                            border: '2px dashed',
                            borderColor: 'primary.main',
                            borderRadius: 2,
                            backgroundColor: 'background.paper',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'primary.light',
                              borderColor: 'primary.dark',
                              transform: 'scale(1.02)',
                            },
                          }}
                          onClick={() => handleRevisionDrop(project.id, revision.id)}
                          onDrop={(e) => {
                            e.preventDefault();
                            handleRevisionDrop(project.id, revision.id);
                          }}
                          onDragOver={(e) => e.preventDefault()}
                          role="button"
                          tabIndex={0}
                          aria-label={`Drop sandbox into ${project.name} - ${revision.revision_name || 'Unknown'} (Step ${revision.revision_step})`}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon />
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {revision.revision_name || 'Unknown'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Step {revision.revision_step}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      ))}

                      {project.revisions.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            No revisions available in this project
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}

              {projects.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No projects available
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Press <strong>Escape</strong> or click outside to cancel
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default DragOverlay;